import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { X, Check, ShoppingBasket, Loader2, PackageCheck } from "lucide-react";

/**
 * Modal that shows the pre-curated Sudanese grocery library.
 * Provider selects items with checkboxes and bulk-adds them to their store.
 */
export const GroceryLibraryModal = ({ open, onClose, providerId, providerAccent = "#84CC16", lang = "en", onAdded }) => {
  const [categories, setCategories] = useState([]);
  const [selected, setSelected] = useState(new Set()); // set of library indices
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [activeCat, setActiveCat] = useState(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setSelected(new Set());
    api.get("/grocery/library")
      .then((r) => {
        setCategories(r.data.categories || []);
        setActiveCat(r.data.categories?.[0]?.key || null);
      })
      .finally(() => setLoading(false));
  }, [open]);

  const toggle = (globalIdx) => {
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(globalIdx)) n.delete(globalIdx);
      else n.add(globalIdx);
      return n;
    });
  };

  // Compute global indices per category by their position in the flat library.
  // The backend endpoint returns items in the same order as GROCERY_LIBRARY, grouped by category.
  const flatList = categories.flatMap((c) => c.items);
  const globalIndexOf = (item) => flatList.indexOf(item);

  const activeCategoryData = categories.find((c) => c.key === activeCat);

  const submit = async () => {
    if (selected.size === 0) return;
    setAdding(true);
    try {
      const { data } = await api.post(`/providers/${providerId}/grocery/bulk-add`, {
        library_indices: Array.from(selected),
      });
      onAdded?.(data.added, data.items);
      onClose();
    } catch (e) {
      // parent handles error banner if needed
      alert(e?.response?.data?.detail || "Failed to add items");
    } finally {
      setAdding(false);
    }
  };

  if (!open) return null;

  const isAr = lang === "ar";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4" data-testid="grocery-library-modal">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative w-full max-w-4xl max-h-[90vh] rounded-2xl overflow-hidden flex flex-col shadow-2xl" style={{ background: "var(--so-surface)" }}>
        {/* Header */}
        <div className="flex items-start justify-between p-5 border-b border-[var(--so-border)]">
          <div>
            <div className="flex items-center gap-2 font-bold text-lg">
              <ShoppingBasket className="w-5 h-5" style={{ color: providerAccent }} />
              {isAr ? "مكتبة البقالة" : "Grocery Library"}
            </div>
            <div className="text-xs text-[var(--so-text-soft)] mt-0.5">
              {isAr
                ? `اختر المنتجات التي يوفرها متجرك. الأسعار قابلة للتعديل لاحقًا.`
                : `Pick the items your store stocks. You can edit prices later.`}
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-[var(--so-border)]/40 flex items-center justify-center" data-testid="close-library">
            <X className="w-4 h-4" />
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="w-6 h-6 animate-spin text-[var(--so-text-soft)]" />
          </div>
        ) : (
          <div className="flex-1 overflow-hidden flex flex-col md:flex-row">
            {/* Category sidebar */}
            <div className="md:w-52 md:border-r border-[var(--so-border)] flex md:flex-col overflow-x-auto md:overflow-y-auto shrink-0 bg-[var(--so-surface-alt,var(--so-surface))]">
              {categories.map((c) => {
                const count = c.items.filter((i) => selected.has(globalIndexOf(i))).length;
                return (
                  <button
                    key={c.key}
                    onClick={() => setActiveCat(c.key)}
                    className={`px-4 py-3 text-start text-sm border-b md:border-b border-[var(--so-border)] whitespace-nowrap ${activeCat === c.key ? "font-bold" : "text-[var(--so-text-soft)]"}`}
                    style={activeCat === c.key ? { background: `${providerAccent}12`, borderInlineStart: `3px solid ${providerAccent}` } : {}}
                    data-testid={`library-cat-${c.key}`}
                  >
                    <div>{isAr ? c.label_ar : c.label_en}</div>
                    <div className="text-[10px] text-[var(--so-text-soft)] mt-0.5">
                      {c.items.length} {isAr ? "منتج" : "items"}
                      {count > 0 && <span className="ms-1 font-bold" style={{ color: providerAccent }}>· {count} {isAr ? "مختار" : "picked"}</span>}
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Items grid */}
            <div className="flex-1 overflow-y-auto p-4">
              {activeCategoryData ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                  {activeCategoryData.items.map((it) => {
                    const gIdx = globalIndexOf(it);
                    const on = selected.has(gIdx);
                    return (
                      <button
                        key={gIdx}
                        onClick={() => toggle(gIdx)}
                        className="relative p-3 rounded-xl border-2 text-start transition-all"
                        style={{
                          borderColor: on ? providerAccent : "var(--so-border)",
                          background: on ? `${providerAccent}0F` : "transparent",
                        }}
                        data-testid={`library-item-${gIdx}`}
                      >
                        {on && (
                          <div className="absolute top-2 end-2 w-5 h-5 rounded-full flex items-center justify-center" style={{ background: providerAccent }}>
                            <Check className="w-3 h-3 text-white" />
                          </div>
                        )}
                        <div className="text-3xl mb-1">{it.image}</div>
                        <div className="text-sm font-semibold leading-tight">{isAr ? it.name_ar : it.name_en}</div>
                        <div className="text-[11px] text-[var(--so-text-soft)] mt-0.5">{isAr ? it.unit_ar : it.unit_en}</div>
                        <div className="text-xs font-bold mt-1" style={{ color: providerAccent }}>
                          {it.price.toLocaleString()} SDG
                        </div>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center text-[var(--so-text-soft)] py-16">{isAr ? "اختر تصنيفًا" : "Select a category"}</div>
              )}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="p-4 border-t border-[var(--so-border)] flex items-center justify-between">
          <div className="text-sm">
            <span className="font-bold" style={{ color: providerAccent }}>{selected.size}</span>{" "}
            <span className="text-[var(--so-text-soft)]">{isAr ? "منتج مختار" : selected.size === 1 ? "item selected" : "items selected"}</span>
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm font-semibold border border-[var(--so-border)]" data-testid="cancel-library">
              {isAr ? "إلغاء" : "Cancel"}
            </button>
            <button
              onClick={submit}
              disabled={selected.size === 0 || adding}
              className="px-5 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-40 inline-flex items-center gap-2"
              style={{ background: providerAccent }}
              data-testid="submit-library"
            >
              {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <PackageCheck className="w-4 h-4" />}
              {isAr ? `أضف ${selected.size} منتج` : `Add ${selected.size} to my store`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GroceryLibraryModal;
